import React from "react";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import { Program } from "../../types/program";
import { replaceHarvardWithIvyLeague } from "../../utils/utils";

interface ProgramFeaturesProps {
  program: Program;
}

const ProgramFeatures: React.FC<ProgramFeaturesProps> = ({ program }) => {
  const featureDescriptions: { [key: string]: string } = {
    "World-Class Academic Oversight":
      "Curricula designed and supervised by University of Cambridge Professors.",
    "Expert On-Site Mentoring":
      "Daily in-person instruction delivered by Cambridge PhD female researchers.",
    "Specialised Subjects":
      "Streams available in Entrepreneurship, Artificial Intelligence (AI), and Medicine.",
    "Exclusive Certification":
      "Certificates of Attendance signed by the supervising Cambridge Professor.",
    "Performance Awards":
      "Recommendation letters awarded to the top 50% of students based on merit.",
    "Small Cohorts":
      "Class sizes strictly limited to a maximum of 17 students per programme to ensure personalised attention.",
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {program.features.map((feature, index) => {
        const featureTitle = replaceHarvardWithIvyLeague(feature);
        const description = Object.keys(featureDescriptions).find(
          (key) =>
            featureTitle.toLowerCase().includes(key.toLowerCase()) ||
            key.toLowerCase().includes(featureTitle.toLowerCase())
        )
          ? featureDescriptions[
              Object.keys(featureDescriptions).find(
                (key) =>
                  featureTitle.toLowerCase().includes(key.toLowerCase()) ||
                  key.toLowerCase().includes(featureTitle.toLowerCase())
              )!
            ]
          : `Comprehensive coverage of ${featureTitle.toLowerCase()} with expert guidance.`;

        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 * index }}
            className="flex items-start space-x-4 p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
          >
            <div className="flex-shrink-0">
              <CheckCircle className="h-6 w-6 text-green-500" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">
                {featureTitle}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {description}
              </p>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default ProgramFeatures;
